/**
 * UPLOAD-WIRE B — the one honest countdown number (§24 kept: one line, no
 * stages, never a failure mid-way).
 *
 * Two phases:
 *   uploading  (no acceptedAt): transfer remainder at the measured rate plus
 *              the server-side estimate for what follows;
 *   processing (acceptedAt set): the server estimate minus wall-clock elapsed
 *              since acceptance, floored at 1 s.
 *
 * The display wrapper (UploadZone) enforces non-increase except the single
 * permitted reset when the 202's estimate replaces the pre-upload estimate;
 * ticks derive from Date.now() so a starved tab catches up instantly instead
 * of lying.
 *
 * @module dashboard/utils/remainingSeconds
 */

// EMA smoothing for the upload rate: ~5 s effective window at 500 ms samples.
export const RATE_EMA_ALPHA = 0.18;
export const MIN_RATE_BPS = 1024; // never divide toward infinity on a stall

/**
 * Update the exponential moving average of the transfer rate.
 * @param {number|null} prevBps - previous EMA (null before the seed)
 * @param {number} deltaBytes - bytes moved since the last sample
 * @param {number} deltaMs - wall-clock span of the sample
 * @returns {number|null} new EMA in bytes/second
 */
export function updateRateBps(prevBps, deltaBytes, deltaMs) {
  if (!Number.isFinite(deltaMs) || deltaMs <= 0) return prevBps;
  const inst = Math.max(0, deltaBytes) / (deltaMs / 1000);
  if (prevBps == null) return inst; // seed from the first sample window
  return prevBps + RATE_EMA_ALPHA * (inst - prevBps);
}

/**
 * The pure remaining-seconds computation.
 *
 * @param {object} s
 * @param {number} s.now - Date.now()
 * @param {number} [s.acceptedAt] - Date.now() when the 202 landed (processing phase)
 * @param {number} [s.loaded] - bytes sent so far (uploading phase)
 * @param {number} [s.total] - total bytes to send
 * @param {number|null} [s.rateBps] - EMA transfer rate
 * @param {number|null} [s.serverEstimate] - queue-status estimate until the
 *   202 arrives, then the 202's estimate_seconds (null → caller keeps prior)
 * @returns {number} whole seconds, ≥ 1
 */
export function remainingSeconds({ now, acceptedAt, loaded, total, rateBps, serverEstimate }) {
  const est = Number.isFinite(serverEstimate) && serverEstimate > 0 ? serverEstimate : 0;
  if (Number.isFinite(acceptedAt) && acceptedAt > 0) {
    const elapsed = Math.max(0, (now - acceptedAt) / 1000);
    return Math.max(1, Math.ceil(est - elapsed));
  }
  const left = Math.max(0, (Number.isFinite(total) ? total : 0) - (Number.isFinite(loaded) ? loaded : 0));
  const rate = Number.isFinite(rateBps) && rateBps > MIN_RATE_BPS ? rateBps : MIN_RATE_BPS;
  const transfer = left > 0 ? Math.ceil(left / rate) : 0;
  return Math.max(1, transfer + Math.ceil(est));
}
